import type { Dispatch, SetStateAction } from 'react';

import { useConfig } from '@proton/app-context/useConfig';
import { getAppHref } from '@proton/shared/lib/apps/helper';
import { getLocalIDFromPathname } from '@proton/shared/lib/authentication/pathnameHelper';
import { addParentAppToUrl, getIsIframedDrawerApp, postMessageToIframe } from '@proton/shared/lib/drawer/helpers';
import type { DrawerApp, IframeSrcMap, OpenDrawerArgs } from '@proton/shared/lib/drawer/interfaces';
import { DRAWER_EVENTS } from '@proton/shared/lib/drawer/interfaces';

import useApiStatus from '../useApiStatus';
import useOnline from '../useOnline';

interface Props {
    appInView: DrawerApp | undefined;
    setAppInView: Dispatch<SetStateAction<DrawerApp | undefined>>;
    iframeSrcMap: IframeSrcMap;
    setIframeSrcMap: Dispatch<SetStateAction<IframeSrcMap>>;
}

const useToggleDrawerApp = ({ appInView, setAppInView, iframeSrcMap, setIframeSrcMap }: Props) => {
    const { offline } = useApiStatus();
    const onlineStatus = useOnline();
    const { APP_NAME: currentApp } = useConfig();

    const isAppReachable = !offline && onlineStatus;

    return (args: OpenDrawerArgs) => () => {
        const { app, path = '/' } = args;
        const isAppOpened = app === appInView;
        const isIframeApp = getIsIframedDrawerApp(app);

        if (isIframeApp) {
            if (isAppOpened) {
                setAppInView(undefined);
            } else if (isAppReachable) {
                setAppInView(app);

                // If we show again the child app that was cached, we post a message to it in case it needs to take some actions upon becoming visible again (like updating its state)
                postMessageToIframe(
                    {
                        type: DRAWER_EVENTS.SHOW,
                    },
                    app
                );

                if (!iframeSrcMap[app] && isAppReachable) {
                    const localID = getLocalIDFromPathname(window.location.pathname);
                    const appHref = getAppHref(path, app, localID);

                    setIframeSrcMap((map) => ({
                        ...map,
                        [app]: addParentAppToUrl(appHref, currentApp),
                    }));
                }
            }
        } else {
            if (isAppOpened) {
                setAppInView(undefined);
            } else if (isAppReachable) {
                setAppInView(app);
            }
        }
    };
};

export default useToggleDrawerApp;
